import { useDictionary, useLocale, useTranslator } from "gemi/client";
import { folioAiShowFreePromise } from "@/app/utils/landingPromise";
import { lazy, useEffect, useRef, useState } from "react";
import { useV2AuthEntry } from "./AuthModalV2";
import { useLandingSession } from "./useLandingSession";
import { font, SwooshWord } from "./_shared";
import { HeroPrompt } from "./HeroPrompt";
import { homeDict } from "./Home.i18n";
import { Why1SVG } from "./icons/Why1";
import { Why2SVG } from "./icons/Why2";
import { Why3SVG } from "./icons/Why3";
import { WhyIllustration } from "./icons/WhyIllustration";

const Why1Lottie = lazy(() =>
  import("./icons/lottie/Why1").then((m) => ({ default: m.Why1 })),
);
const Why2Lottie = lazy(() =>
  import("./icons/lottie/Why2").then((m) => ({ default: m.Why2 })),
);
const Why3Lottie = lazy(() =>
  import("./icons/lottie/Why3").then((m) => ({ default: m.Why3 })),
);

const DONE_CARDS = [
  {
    titleKey: "done_card_bring",
    descKey: "done_card_bring_desc",
    img: "/v2/img/pottery.jpg",
  },
  {
    titleKey: "done_card_catalog",
    descKey: "done_card_catalog_desc",
    img: "/v2/img/hand-phone.jpg",
  },
  {
    titleKey: "done_card_world",
    descKey: "done_card_world_desc",
    img: "/v2/img/product-page.jpg",
  },
] as const;
/* Category-preview clips. The re-encoded {slug}.webm (VP9, offered first) and
 * {slug}.mp4 (H.264, Safari/iOS fallback) stream through /api/landing-video —
 * gemi's static server does not serve video extensions, so they cannot be reached
 * as plain public/ files. The first-frame {slug}.jpg poster IS a static asset. */
const BUILD_CARDS = [
  { labelKey: "build_card_jewelry", slug: "jewelry" },
  { labelKey: "build_card_fashion", slug: "fashion" },
  { labelKey: "build_card_furniture", slug: "furniture" },
  { labelKey: "build_card_curtain", slug: "curtain" },
] as const;
const PHOTO_CARDS = [
  {
    labelKey: "photo_card_catalog",
    img: "/v2/img/photo/catalog-super-agent.jpg",
  },
  {
    labelKey: "photo_card_editorial",
    img: "/v2/img/photo/editorial-photo.jpg",
  },
  { labelKey: "photo_card_fabric", img: "/v2/img/photo/fabric-visualizer.jpg" },
  {
    labelKey: "photo_card_studio",
    img: "/v2/img/photo/studio-composition.jpg",
  },
  {
    labelKey: "photo_card_closeup",
    img: "/v2/img/photo/close-up-photography.jpg",
  },
] as const;
const VIDEO_CARDS = [
  {
    labelKey: "video_card_from_image",
    img: "/v2/img/video/generate-video-from-image.jpg",
  },
  {
    labelKey: "video_card_editorial",
    img: "/v2/img/video/editorial-product-video.jpg",
  },
  {
    labelKey: "video_card_product",
    img: "/v2/img/video/product-to-video.jpg",
  },
  { labelKey: "video_card_text", img: "/v2/img/video/text-to-video.jpg" },
  { labelKey: "video_card_creator", img: "/v2/img/video/video-creator.jpg" },
] as const;
const SHIP_CARDS = [
  {
    labelKey: "ship_card_instagram",
    img: "/v2/img/ship/instagram-content.jpg",
  },
  { labelKey: "ship_card_banner", img: "/v2/img/ship/banner-designer.jpg" },
  { labelKey: "ship_card_portrait", img: "/v2/img/ship/dramatic-portrait.jpg" },
  {
    labelKey: "ship_card_headshot",
    img: "/v2/img/ship/professional-headshot.jpg",
  },
] as const;
const HERO_META = [
  { key: "meta_credits", icon: "star" },
  { key: "meta_scroll", icon: "arrow-circle-down" },
] as const;

/* ---- reusable bits ---- */
/** Autoplay/muted/looping product-preview video for the "build a product page" cards.
 *  The four videos would otherwise all fetch on page load; the IntersectionObserver mounts
 *  the <video> (and so starts its network request) only once the card nears the viewport,
 *  and pauses it again off-screen. The poster paints the still first frame the moment the
 *  element mounts, so there is no black/neutral flash while the clip decodes; the webm
 *  source is offered before the mp4 so supporting browsers take the smaller VP9 file. */
function BuildCardVideo({ slug, label }: { slug: string; label: string }) {
  const enterApp = useV2AuthEntry();
  const wrapperRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setMounted(true);
          videoRef.current?.play().catch(() => {});
        } else {
          videoRef.current?.pause();
        }
      },
      { rootMargin: "200px", threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // React doesn't always reflect the `muted` JSX prop as the element property, and some
  // browsers then block muted autoplay — set it explicitly and kick playback on mount.
  useEffect(() => {
    const video = videoRef.current;
    if (!mounted || !video) return;
    video.muted = true;
    video.play().catch(() => {});
  }, [mounted]);

  return (
    <button
      ref={wrapperRef}
      type="button"
      onClick={() => enterApp({ target: "ai-studio" })}
      className="relative block h-[500px] w-[316px] flex-none overflow-hidden rounded-[10px] border-x border-t border-[#f0b04d] bg-[#ece9e6] text-left lg:w-auto lg:flex-1"
    >
      {mounted && (
        <video
          ref={videoRef}
          poster={`/v2/video/${slug}.jpg`}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
          className="absolute inset-0 size-full object-cover"
        >
          <source
            src={`/api/landing-video/${slug}/webm`}
            type='video/webm; codecs="vp9"'
          />
          <source src={`/api/landing-video/${slug}/mp4`} type="video/mp4" />
        </video>
      )}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(102,102,102,0) 0%, rgba(0,0,0,0.77) 100%)",
        }}
      />
      <span
        className="absolute bottom-3 left-2 text-[20px] font-medium leading-[0.9] tracking-[-1px] text-white"
        style={font.display}
      >
        {label}
      </span>
    </button>
  );
}

/** Gold-bordered photo card with an Onest white label (agent scroll rows).
 *  Every agent card routes a visitor to sign-up — you create the asset once signed in. */
function AgentCard({
  img,
  label,
  cardClass,
}: {
  img: string;
  label: string;
  cardClass: string;
}) {
  const enterApp = useV2AuthEntry();
  return (
    <button
      type="button"
      onClick={() => enterApp({ target: "ai-studio" })}
      className={`relative block flex-none overflow-hidden rounded-[10px] border-x border-t border-[#f0b04d] text-left ${cardClass}`}
    >
      <img
        src={img}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 size-full object-cover"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(102,102,102,0) 0%, rgba(0,0,0,0.77) 100%)",
        }}
      />
      <span
        className="absolute bottom-3 left-2 text-[20px] font-medium leading-[0.9] tracking-[-1px] text-white"
        style={font.display}
      >
        {label}
      </span>
    </button>
  );
}

/** Section: title + horizontal photo-card scroll + full-width dark CTA (Figma mobile). */
function ScrollSection({
  title,
  cta,
  cardClass,
  cards,
}: {
  title: string;
  cta: string;
  cardClass: string;
  cards: { label: string; img: string }[];
}) {
  const enterApp = useV2AuthEntry();
  return (
    <section className="px-5 py-16 lg:px-16 lg:py-12">
      <div className="mx-auto flex max-w-[1312px] flex-col gap-12">
        <div className="flex items-end justify-between gap-4">
          <h2
            className="m-0 text-[36px] font-medium leading-[1.05] tracking-[-1.44px] text-[#343139] lg:text-[48px] lg:leading-none lg:tracking-[-1.92px]"
            style={font.display}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={() => enterApp({ target: "ai-studio" })}
            className="hidden flex-none items-center justify-center gap-1 whitespace-nowrap rounded-full bg-[#343339] px-8 py-5 text-[14px] font-medium text-white lg:flex"
          >
            {cta} <span aria-hidden>→</span>
          </button>
        </div>
        <div className="flex flex-col gap-6">
          <div className="no-scrollbar -mx-5 flex gap-4 overflow-x-auto px-5 lg:mx-0 lg:overflow-visible lg:px-0">
            {cards.map((c) => (
              <AgentCard
                key={c.label}
                img={c.img}
                label={c.label}
                cardClass={cardClass}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => enterApp({ target: "ai-studio" })}
            className="flex h-[50px] w-full items-center justify-center gap-1 rounded-full bg-[#343339] px-8 text-[14px] font-medium text-white lg:hidden"
          >
            {cta} <span aria-hidden>→</span>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---- page ---- */
export default function Home() {
  const t = useTranslator("HomePageV2");
  const tHome = useDictionary(homeDict);
  const enterApp = useV2AuthEntry();
  const [locale] = useLocale();
  const { user, loading: userLoading } = useLandingSession();

  // The server already redirects a signed-in visitor's landing document. This covers what it cannot
  // see: a landing the browser still holds from before sign-in (it is cached for hours), and a soft
  // navigation here, whose route data does not redirect. /app/redirect resolves the org and handles
  // the org-less case; a hard location change, like CustomerRedirect, since we are leaving the SPA
  // landing entirely.
  useEffect(() => {
    if (!userLoading && user) {
      window.location.href = "/app/redirect";
    }
  }, [userLoading, user]);

  const features = [
    {
      title: t("feat1_title"),
      desc: t("feat1_desc"),
      svg: <Why1SVG />,
      lottie: <Why1Lottie />,
    },
    {
      title: t("feat2_title"),
      desc: t("feat2_desc"),
      svg: <Why2SVG />,
      lottie: <Why2Lottie />,
    },
    {
      title: t("feat3_title"),
      desc: t("feat3_desc"),
      svg: <Why3SVG />,
      lottie: <Why3Lottie />,
    },
  ];
  const tools = [
    {
      title: t("tool1_title"),
      sub: t("tool1_sub"),
      desc: t("tool1_desc"),
      img: "/v2/img/tools/sme-manufacturers.jpg",
      tags: [t("tag_sme_1"), t("tag_sme_2"), t("tag_sme_3")],
    },
    {
      title: t("tool2_title"),
      sub: t("tool2_sub"),
      desc: t("tool2_desc"),
      img: "/v2/img/tools/ecommerce-players.jpg",
      tags: [t("tag_ecom_1"), t("tag_ecom_2"), t("tag_ecom_3")],
    },
    {
      title: t("tool3_title"),
      sub: t("tool3_sub"),
      desc: t("tool3_desc"),
      img: "/v2/img/tools/solo-entrepreneurs.jpg",
      tags: [t("tag_solo_1"), t("tag_solo_2"), t("tag_solo_3")],
    },
    {
      title: t("tool4_title"),
      sub: t("tool4_sub"),
      desc: t("tool4_desc"),
      img: "/v2/img/tools/small-retailers.jpg",
      tags: [t("tag_retail_1"), t("tag_retail_2"), t("tag_retail_3")],
    },
  ];
  const stats = [
    { num: "3500+", label: t("stat1_label") },
    { num: "15000+", label: t("stat2_label") },
    { num: "28", label: t("stat3_label") },
  ];

  return (
    <>
      {/* HERO */}
      <section className="relative z-10 flex flex-col items-stretch px-5 pb-16 pt-16 text-left lg:items-center lg:px-16 lg:pb-[90px] lg:pt-14 lg:text-center">
        <h1
          className="m-0 text-[48px] font-medium leading-[1.05] tracking-[-1.92px] text-[#343339] lg:text-[64px] lg:leading-[1.1] lg:tracking-[-2.56px]"
          style={font.display}
        >
          {t("hero_title_a")}
          <SwooshWord id="heroSwoosh">{t("hero_title_today")}</SwooshWord>
          {t("hero_title_b")}
        </h1>
        <p className="m-0 mt-4 max-w-[640px] text-[16px] leading-[1.4] text-[#343339] opacity-80 lg:mx-auto lg:mt-5 lg:text-[18px]">
          {tHome("hero_subtitle")}
        </p>

        <HeroPrompt />

        <div className="mt-4 flex flex-col gap-2.5 opacity-60 lg:mt-[27px] lg:flex-row lg:gap-[30px]">
          {(folioAiShowFreePromise()
            ? HERO_META
            : HERO_META.filter((m) => m.key !== "meta_credits")
          ).map((m) => (
            <span key={m.key} className="flex items-center gap-1.5">
              <img
                src={`/v2/icons/${m.icon}.svg`}
                alt=""
                aria-hidden
                className="block size-5"
              />
              <span className="text-[14px] font-medium tracking-[0.14px] text-[#343339]">
                {t(m.key)}
              </span>
            </span>
          ))}
        </div>
      </section>

      {/* WHY FOLIO — feature icons are large Figma illustrations */}
      <section className="px-5 py-16 text-center lg:px-16 lg:pb-28 lg:pt-12">
        <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-12 lg:gap-20">
          <div className="flex flex-col items-center gap-4">
            <span
              className="text-[16px] text-[#84828c] lg:text-[14px]"
              style={font.display}
            >
              {t("why_label")}
            </span>
            <h2
              className="m-0 text-[36px] font-medium leading-[1.05] tracking-[-1.44px] text-[#343139] lg:text-[48px] lg:tracking-[-1.92px]"
              style={font.display}
            >
              {t("why_title")}
            </h2>
          </div>
          <div className="flex flex-col items-center gap-[109px] lg:grid lg:grid-cols-3 lg:items-start">
            {features.map((f) => (
              <div
                key={f.title}
                className="flex flex-col items-center gap-12 text-center"
              >
                <WhyIllustration svg={f.svg} lottie={f.lottie} />
                <div className="flex flex-col items-center gap-2">
                  <h3
                    className="m-0 text-[26px] font-semibold text-[#343339]"
                    style={font.display}
                  >
                    {f.title}
                  </h3>
                  <p className="m-0 max-w-[254px] text-[16px] leading-[1.3] text-[#343339] opacity-80">
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT KNOWLEDGE TO AGENT-READY COMMERCE */}
      <section className="px-5 py-16 lg:px-16 lg:py-12">
        <div className="mx-auto flex max-w-[1312px] flex-col gap-12">
          <h2
            className="m-0 text-[36px] font-medium leading-[1.05] tracking-[-1.44px] text-[#343139] lg:text-center lg:text-[54px] lg:leading-none lg:tracking-[-2.16px]"
            style={font.display}
          >
            {t("sec_done")}
          </h2>
          <ol
            role="list"
            className="no-scrollbar -mx-5 m-0 flex list-none gap-[14px] overflow-x-auto px-5 lg:mx-0 lg:overflow-visible lg:px-0"
          >
            {DONE_CARDS.map((c) => (
              <li
                key={c.titleKey}
                className="relative h-[320px] w-[268px] flex-none overflow-hidden rounded-[10px] lg:aspect-[428/320] lg:h-auto lg:w-auto lg:flex-1"
              >
                <img
                  src={c.img}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 size-full object-cover"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(102,102,102,0) 0%, rgba(0,0,0,0.77) 100%)",
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-4 text-white">
                  <h3
                    className="m-0 text-[24px] font-medium leading-[0.95] tracking-[-1px] lg:text-[20px]"
                    style={font.display}
                  >
                    {tHome(c.titleKey)}
                  </h3>
                  <p className="m-0 text-[14px] leading-[1.3]">
                    {tHome(c.descKey)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* BUILD A PRODUCT PAGE */}
      <section className="px-5 py-16 lg:px-16 lg:py-12">
        <div className="mx-auto flex max-w-[1312px] flex-col gap-12">
          <div className="flex items-end justify-between gap-4">
            <h2
              className="m-0 text-[36px] font-medium leading-[1.05] tracking-[-1.44px] text-[#343139] lg:text-[48px] lg:leading-none lg:tracking-[-1.92px]"
              style={font.display}
            >
              {t("sec_build")}
            </h2>
            <button
              type="button"
              onClick={() => enterApp({ target: "ai-studio" })}
              className="hidden flex-none items-center justify-center gap-1 whitespace-nowrap rounded-full bg-[#343339] px-8 py-5 text-[14px] font-medium text-white lg:flex"
            >
              {t("build_cta")} <span aria-hidden>→</span>
            </button>
          </div>
          <div className="flex flex-col gap-6">
            <div className="no-scrollbar -mx-5 flex gap-4 overflow-x-auto px-5 lg:mx-0 lg:px-0">
              {BUILD_CARDS.map((c) => (
                <BuildCardVideo
                  key={c.labelKey}
                  slug={c.slug}
                  label={t(c.labelKey)}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => enterApp({ target: "ai-studio" })}
              className="flex h-[50px] w-full items-center justify-center gap-1 rounded-full bg-[#343339] px-8 text-[14px] font-medium text-white lg:hidden"
            >
              {t("build_cta")} <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      </section>

      {/* SCROLL ROWS */}
      <ScrollSection
        title={t("sec_photo")}
        cta={t("photo_cta")}
        cardClass="size-[250px] lg:h-auto lg:w-auto lg:aspect-square lg:flex-1"
        cards={PHOTO_CARDS.map((c) => ({ label: t(c.labelKey), img: c.img }))}
      />
      <ScrollSection
        title={t("sec_video")}
        cta={t("video_cta")}
        cardClass="h-[250px] w-[251px] lg:h-auto lg:w-auto lg:aspect-square lg:flex-1"
        cards={VIDEO_CARDS.map((c) => ({ label: t(c.labelKey), img: c.img }))}
      />
      <ScrollSection
        title={t("sec_ship")}
        cta={t("ship_cta")}
        cardClass="h-[500px] w-[316px] lg:h-auto lg:w-auto lg:aspect-[316/500] lg:flex-1"
        cards={SHIP_CARDS.map((c) => ({ label: t(c.labelKey), img: c.img }))}
      />

      {/* TRUSTED BY (SEC 07) — dark gradient band + heading + investor logos (byte-exact Figma SVGs) */}
      <section className="bg-gradient-to-b from-[#0a0a0a] from-[32%] to-[#343339] px-5 py-16 lg:px-16 lg:py-12">
        <div className="flex flex-col items-center gap-12">
          <p
            className="m-0 text-center text-[16px] font-light uppercase leading-[1.3] tracking-[0.64px] text-white lg:text-[14px]"
            style={font.mono}
          >
            {t("backed_by")}
          </p>
          <div className="flex flex-col items-center gap-9 lg:flex-row lg:gap-x-[85px]">
            <img
              src="/v2/logos/wordmark-1.svg"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="h-[23px] w-auto"
            />
            <img
              src="/v2/logos/mark-2.svg"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="size-10"
            />
            <img
              src="/v2/logos/wordmark-3.svg"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="h-[23px] w-auto"
            />
            <img
              src="/v2/logos/wordmark-4.svg"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="h-[23px] w-auto"
            />
            <img
              src="/v2/logos/wordmark-5.svg"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="h-[23px] w-auto"
            />
          </div>
        </div>
      </section>

      {/* TOOLS (SEC 09) — dark cards with real photos + localized tag chips (desktop only) */}
      <section className="px-5 py-16 lg:px-16">
        <div className="mx-auto flex max-w-[1312px] flex-col gap-12">
          <h2
            className="m-0 text-[36px] font-medium leading-[1.05] tracking-[-1.44px] text-[#343139] lg:text-[48px] lg:leading-none lg:tracking-[-1.92px]"
            style={font.display}
          >
            {t("sec_tools")}
          </h2>
          <div className="flex flex-col gap-5 lg:grid lg:grid-cols-2">
            {tools.map((tool) => (
              <div
                key={tool.title}
                className="flex flex-col justify-center gap-[30px] overflow-hidden rounded-[20px] bg-[#343139] px-[30px] py-[52px] lg:h-[607px]"
              >
                <div className="flex flex-col gap-9 text-white">
                  <div className="flex flex-col gap-4">
                    <h3
                      className="m-0 text-[32px] font-medium leading-[0.9] tracking-[-1.28px]"
                      style={font.body}
                    >
                      {tool.title}
                    </h3>
                    <span className="text-[16px] text-white/80 lg:text-[14px]">
                      {tool.sub}
                    </span>
                  </div>
                  <p className="m-0 text-[20px] font-light leading-[1.3]">
                    {tool.desc}
                  </p>
                </div>
                <div className="h-[259px] w-full overflow-hidden rounded-[20px] lg:h-auto lg:min-h-0 lg:flex-1 lg:rounded-[30px]">
                  <img
                    src={tool.img}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="size-full object-cover"
                  />
                </div>
                {/* lang drives locale-correct CSS text-transform casing (tr i→İ) on the
                    uppercased tag label and chips; the document <html lang> does not
                    update on in-page locale toggle. */}
                <div lang={locale} className="hidden flex-col gap-4 lg:flex">
                  <span
                    className="text-[14px] uppercase tracking-[0.56px] text-white/80"
                    style={{ ...font.mono, fontWeight: 300 }}
                  >
                    {t("best_with")}
                  </span>
                  <div className="flex gap-2 opacity-80">
                    {tool.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-[10px] border border-white px-2 py-1.5 text-[10px] uppercase tracking-[0.4px] text-white"
                        style={{ ...font.mono, fontWeight: 300 }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS (SEC 10) — dark card with gradient-text numbers */}
      <section className="px-5 py-16 lg:px-16">
        <div className="mx-auto max-w-[1129px]">
          <div className="flex flex-col rounded-[20px] bg-[#343339] px-12 py-[30px] lg:flex-row lg:p-12">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`flex flex-col items-center gap-4 py-4 text-center lg:flex-1 lg:py-0 ${
                  i < stats.length - 1
                    ? "border-b-[0.5px] border-[rgba(255,255,255,0.5)] lg:border-b-0 lg:border-r-[0.5px]"
                    : ""
                }`}
              >
                <span
                  className="bg-gradient-to-b from-[#e3f0fe] to-[#ece8e3] bg-clip-text text-[48px] font-medium leading-[52px] tracking-[-2px] text-transparent"
                  style={font.display}
                >
                  {s.num}
                </span>
                <span className="max-w-[211px] text-[14px] text-white/80">
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
